/**
 * Builds/updates List of Tables and List of Figures directly from editor HTML.
 * This runs in the export pipeline so existing editor behavior stays intact.
 */

const TABLE_LIST_SECTION_ID = 'wf-generated-list-of-tables';
const FIGURE_LIST_SECTION_ID = 'wf-generated-list-of-figures';

function normalizeWhitespace(value = '') {
	return String(value).replace(/\s+/g, ' ').trim();
}

function stripCaptionPrefix(text, type) {
	const source = normalizeWhitespace(text);
	if (!source) return '';
	const pattern =
		type === 'table'
			? /^table\s+\d+\s*[:.-]?\s*/i
			: /^figure\s+\d+\s*[:.-]?\s*/i;
	return source.replace(pattern, '').trim();
}

function formatCaption(type, index, content) {
	const label = type === 'table' ? 'Table' : 'Figure';
	const core = stripCaptionPrefix(content, type);
	return core ? `${label} ${index}. ${core}` : `${label} ${index}.`;
}

function isHeadingNode(node) {
	return /^H[1-6]$/.test(node?.tagName || '');
}

function isCaptionCandidate(node) {
	if (!node || node.nodeType !== 1) return false;
	if (isHeadingNode(node)) return false;
	if (node.closest('.mce-toc')) return false;
	const text = normalizeWhitespace(node.textContent);
	return !!text;
}

function findSiblingCaption(node, type) {
	const expectedPrefix = type === 'table' ? /^table\b/i : /^figure\b/i;
	const className = type === 'table' ? 'wf-table-caption' : 'wf-figure-caption';

	const next = node.nextElementSibling;
	if (next && isCaptionCandidate(next)) {
		if (next.classList.contains(className) || expectedPrefix.test(normalizeWhitespace(next.textContent))) {
			return next;
		}
	}

	const prev = node.previousElementSibling;
	if (prev && isCaptionCandidate(prev)) {
		if (prev.classList.contains(className) || expectedPrefix.test(normalizeWhitespace(prev.textContent))) {
			return prev;
		}
	}

	return null;
}

function ensureTableMetadata(root) {
	const items = [];
	let index = 1;
	const tables = root.querySelectorAll('table:not(.mce-toc):not(.wf-approval-table)');
	tables.forEach((table) => {
		if (table.closest('.mce-toc') || table.closest('[data-wf-generated-list]')) return;

		let captionNode = findSiblingCaption(table, 'table');
		if (!captionNode && table.caption) {
			captionNode = table.caption;
		}

		const normalized = formatCaption('table', index, captionNode?.textContent || '');
		if (captionNode) {
			captionNode.textContent = normalized;
			captionNode.classList.add('wf-table-caption', 'wf-no-indent');
		}

		const anchorId = table.id || `wf-table-${index}`;
		table.id = anchorId;
		table.setAttribute('data-table-index', String(index));
		items.push({ number: index, text: normalized, anchorId });
		index += 1;
	});
	return items;
}

function ensureFigureMetadata(root) {
	const items = [];
	let index = 1;
	const figures = root.querySelectorAll('figure, img');
	figures.forEach((node) => {
		if (node.closest('.mce-toc') || node.closest('[data-wf-generated-list]')) return;
		const figure = node.tagName === 'FIGURE' ? node : null;
		const image = node.tagName === 'IMG' ? node : node.querySelector?.('img');
		if (!image) return;
		if (image.closest('table')) return; // do not treat table-embedded images as figures

		let captionNode = null;
		if (figure) {
			captionNode = figure.querySelector('figcaption');
		}
		if (!captionNode) {
			captionNode = findSiblingCaption(figure || image, 'figure');
		}

		const normalized = formatCaption('figure', index, captionNode?.textContent || image.alt || '');
		if (captionNode) {
			captionNode.textContent = normalized;
			captionNode.classList.add('wf-figure-caption', 'wf-no-indent');
		}

		const anchorId = (figure || image).id || `wf-figure-${index}`;
		(figure || image).id = anchorId;
		(figure || image).setAttribute('data-figure-index', String(index));
		items.push({ number: index, text: normalized, anchorId });
		index += 1;
	});
	return items;
}

function buildListSectionHtml(sectionId, title, kind, items) {
	const listItemsHtml =
		items.length === 0
			? `<li class="wf-generated-list-empty">No ${kind} in this document.</li>`
			: items
					.map(
						(item) => `<li class="wf-generated-list-item">
	<a href="#${item.anchorId}" class="wf-generated-list-title">${item.text}</a>
	<span class="wf-generated-list-leader" aria-hidden="true"></span>
	<span class="wf-generated-list-page">#</span>
</li>`
					)
					.join('');

	return `<section id="${sectionId}" data-wf-generated-list="${kind}" class="wf-generated-list-block page-break-before">
	<h2>${title}</h2>
	<ol class="wf-generated-list">${listItemsHtml}</ol>
</section>`;
}

function upsertGeneratedSection(root, sectionId, title, kind, items) {
	const parser = root.ownerDocument;
	const html = buildListSectionHtml(sectionId, title, kind, items);

	const existing = root.querySelector(`#${sectionId}`);
	if (existing) {
		const wrapper = parser.createElement('div');
		wrapper.innerHTML = html;
		existing.replaceWith(wrapper.firstElementChild);
		return;
	}

	const anchor = root.querySelector('.mce-toc')?.closest('div,section,article') || root.querySelector('.mce-toc');
	if (anchor && anchor.parentNode) {
		const wrapper = parser.createElement('div');
		wrapper.innerHTML = html;
		anchor.parentNode.insertBefore(wrapper.firstElementChild, anchor.nextSibling);
		return;
	}

	root.insertAdjacentHTML('beforeend', html);
}

export function applyTableFigureListsToExportHtml(html) {
	if (!html || typeof html !== 'string') return html;
	try {
		const parser = new DOMParser();
		const doc = parser.parseFromString(`<div id="wf-export-root">${html}</div>`, 'text/html');
		const root = doc.getElementById('wf-export-root');
		if (!root) return html;

		const tables = ensureTableMetadata(root);
		const figures = ensureFigureMetadata(root);

		const hasGeneratedTableSection = !!root.querySelector(`#${TABLE_LIST_SECTION_ID}`);
		const hasGeneratedFigureSection = !!root.querySelector(`#${FIGURE_LIST_SECTION_ID}`);

		// Thesis templates already include List of Tables / Figures. Do not inject a second
		// generated block (those use "#" page placeholders and create an orphan page).
		const hasNativeTableList = Array.from(root.querySelectorAll('h1,h2,h3,p,strong')).some((el) =>
			/^list\s+of\s+tables$/i.test((el.textContent || '').replace(/\s+/g, ' ').trim())
		);
		const hasNativeFigureList = Array.from(root.querySelectorAll('h1,h2,h3,p,strong')).some((el) =>
			/^list\s+of\s+figures$/i.test((el.textContent || '').replace(/\s+/g, ' ').trim())
		);

		if (hasGeneratedTableSection || (tables.length > 0 && !hasNativeTableList)) {
			upsertGeneratedSection(root, TABLE_LIST_SECTION_ID, 'List of Tables', 'tables', tables);
		}
		if (hasGeneratedFigureSection || (figures.length > 0 && !hasNativeFigureList)) {
			upsertGeneratedSection(root, FIGURE_LIST_SECTION_ID, 'List of Figures', 'figures', figures);
		}

		return root.innerHTML;
	} catch {
		return html;
	}
}

